<ng-container *ngIf="!loading; else loadingTemplate">
  <form (ngSubmit)="(close)" [formGroup]="form">
    <!-- Header générique -->
    <base-form-header [mode]="crudModeEnum.Update" [updateLabel]="headerLabel"></base-form-header>

    <!-- Stepper -->
    <mat-stepper linear #stepper style="width: 100%">
      <!-- STEP 1: Configuration -->
      <mat-step [stepControl]="form" [editable]="true">
        <ng-template matStepLabel>Configuration</ng-template>

        <form [formGroup]="form" class="p-6 space-y-6">
          <!-- Avertissement compact -->
          <div class="bg-amber-50 border-l-4 border-amber-400 p-4">
            <div class="flex">
              <mat-icon class="mr-3 mt-0.5" color="warn">warning</mat-icon>
              <div>
                <h3 class="text-sm font-medium text-amber-800">Cette action peut impacter les engagements associés</h3>
              </div>
            </div>
          </div>

          <!-- Établissement support -->
          <mat-form-field appearance="fill" style="width: 100%">
            <mat-label>Établissement support concerné</mat-label>
            <input
              matInput
              [value]="data.establishment.technicalIdentifier + ' - ' + data.establishment.label"
              [disabled]="true"
            />
          </mat-form-field>

          <!-- Établissement principal actuel (si existant) -->
          <mat-form-field *ngIf="hasMainEstablishment" appearance="fill" style="width: 100%">
            <mat-label>Établissement principal actuel</mat-label>
            <input
              matInput
              [value]="currentMainEstablishment?.technicalIdentifier + ' - ' + currentMainEstablishment?.label"
              [disabled]="true"
            />
          </mat-form-field>

          <!-- Options de configuration -->
          <div class="space-y-4">
            <h4 class="text-sm font-medium text-gray-900">
              {{ hasMainEstablishment ? 'Nouvelle configuration' : "Configuration de l'établissement principal" }}
            </h4>

            <!-- Mode Ajout : Affichage simplifié sans radio-buttons -->
            <ng-container *ngIf="!hasMainEstablishment">
              <div class="border rounded-lg p-4 bg-blue-50 border-blue-200">
                <h5 class="text-sm font-medium text-gray-900 mb-2">Sélectionner un établissement principal</h5>
                <p class="text-xs text-gray-600 mb-3">
                  Les informations légales seront héritées de l'établissement sélectionné
                </p>

                <base-form-input
                  controlName="mainEstablishment"
                  label="Rechercher un établissement"
                  type="autocomplete"
                  placeholder="Saisir le nom de l'établissement..."
                  [items$]="establishmentItems$"
                  displayProperty="label"
                  (autocompleteChange)="establishmentAutocompleteChange($event)"
                  appearance="outline"
                  class="max-w-md"
                  [formGroup]="form"
                ></base-form-input>
              </div>
            </ng-container>

            <!-- Mode Modification : Affichage avec choix entre lié et indépendant -->
            <ng-container *ngIf="hasMainEstablishment">
              <!-- Option 1: Lier à un établissement existant -->
              <div
                class="radio-option border rounded-lg p-4"
                [class.bg-blue-50]="selectionMode === 'linked'"
                [class.border-blue-200]="selectionMode === 'linked'"
                [class.selected]="selectionMode === 'linked'"
              >
                <div class="flex items-start space-x-3">
                  <mat-radio-button
                    [checked]="selectionMode === 'linked'"
                    (change)="onModeChange('linked')"
                    color="primary"
                  ></mat-radio-button>
                  <div class="flex-1">
                    <h5 class="text-sm font-medium text-gray-900">Lier à un établissement existant</h5>
                    <p class="text-xs text-gray-600 mb-3">
                      Les informations légales seront héritées de l'établissement sélectionné
                    </p>

                    <base-form-input
                      *ngIf="selectionMode === 'linked'"
                      controlName="mainEstablishment"
                      label="Rechercher un établissement"
                      type="autocomplete"
                      placeholder="Saisir le nom de l'établissement..."
                      [items$]="establishmentItems$"
                      displayProperty="label"
                      (autocompleteChange)="establishmentAutocompleteChange($event)"
                      appearance="outline"
                      class="max-w-md slide-in"
                      [formGroup]="form"
                    ></base-form-input>
                  </div>
                </div>
              </div>

              <!-- Option 2: Mode indépendant (uniquement en modification) -->
              <div
                class="radio-option border rounded-lg p-4"
                [class.bg-green-50]="selectionMode === 'independent'"
                [class.border-green-200]="selectionMode === 'independent'"
                [class.selected]="selectionMode === 'independent'"
              >
                <div class="flex items-start space-x-3">
                  <mat-radio-button
                    [checked]="selectionMode === 'independent'"
                    (change)="onModeChange('independent')"
                    color="primary"
                  ></mat-radio-button>
                  <div class="flex-1">
                    <h5 class="text-sm font-medium text-gray-900">Rendre indépendant</h5>
                    <p class="text-xs text-gray-600 mb-3">L'établissement aura ses propres informations légales</p>

                    <div *ngIf="selectionMode === 'independent'" class="slide-in">
                      <div class="mb-4 flex items-center gap-2">
                        <mat-checkbox formControlName="isFrenchCompany">Établissement français</mat-checkbox>
                        <mat-icon
                          matTooltip="Cochez pour un établissement français (SIRET/SIREN) ou décochez pour un établissement étranger (Identifiant entreprise)"
                          matTooltipPosition="above"
                          class="text-gray-400 cursor-help"
                          style="font-size: 18px; height: 18px; width: 18px"
                        >
                          info_outline
                        </mat-icon>
                      </div>

                      <div *ngIf="form.get('isFrenchCompany')?.value" class="form-grid">
                        <div class="field-with-top-hint">
                          <div class="top-hint mat-hint">&nbsp;</div>
                          <base-form-input
                            controlName="siretNumber"
                            label="N° SIRET"
                            type="text"
                            placeholder="12345678901234"
                            appearance="outline"
                            invalidPatternMessage="Le numéro SIRET doit contenir 14 chiffres"
                            [formGroup]="form"
                          ></base-form-input>
                        </div>

                        <div class="field-with-top-hint">
                          <div class="top-hint mat-hint">Auto-complété depuis le SIRET</div>
                          <base-form-input
                            controlName="sirenNumber"
                            label="N° SIREN"
                            type="text"
                            placeholder="123456789"
                            appearance="outline"
                            [disabled]="true"
                            [formGroup]="form"
                          ></base-form-input>
                        </div>
                      </div>

                      <base-form-input
                        *ngIf="!form.get('isFrenchCompany')?.value"
                        controlName="dunsNumber"
                        label="Identifiant entreprise"
                        type="text"
                        placeholder="Identifiant DUNS"
                        appearance="outline"
                        [formGroup]="form"
                      ></base-form-input>

                      <base-form-input
                        controlName="tva"
                        label="N° TVA"
                        type="text"
                        placeholder="FR12345678901"
                        appearance="outline"
                        [formGroup]="form"
                      ></base-form-input>
                    </div>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>
        </form>
      </mat-step>

      <!-- STEP 2: Analyse des dépendances -->
      <mat-step [editable]="true">
        <ng-template matStepLabel>Analyse d'impact</ng-template>

        <div *ngIf="!analysisLoading; else analysisLoadingTemplate">
          <!-- Résumé de l'analyse -->
          <div class="analysis-summary mb-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Analyse d'impact sur les engagements</h3>

            <div class="grid grid-cols-2 gap-4 mb-6">
              <div class="stat-card bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <div class="text-2xl font-bold text-green-600">{{ compatibleContracts.length }}</div>
                <div class="text-sm text-green-700">Engagements compatibles</div>
                <div class="text-xs text-green-600 mt-1">Aucun impact</div>
              </div>
              <div class="stat-card bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                <div class="text-2xl font-bold text-orange-600">{{ problematicContracts.length }}</div>
                <div class="text-sm text-orange-700">Engagements problématiques</div>
                <div class="text-xs text-orange-600 mt-1">Nécessitent une action</div>
              </div>
            </div>

            <!-- Avertissement si il y a des contrats problématiques -->
            <div *ngIf="problematicContracts.length > 0" class="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
              <div class="flex">
                <mat-icon class="mr-3 mt-0.5" color="warn">warning</mat-icon>
                <div>
                  <h4 class="text-sm font-medium text-orange-800">
                    {{ problematicContracts.length }} engagement(s) seront impacté(s)
                  </h4>
                  <p class="text-sm text-orange-700 mt-1">
                    Ces contrats ne seront plus dans le périmètre du nouvel établissement principal. Vous devrez soit
                    étendre le périmètre de ces contrats, soit accepter leur restriction.
                  </p>
                </div>
              </div>
            </div>

            <!-- Message de succès si tout est compatible -->
            <div
              *ngIf="problematicContracts.length === 0 && currentEstablishmentContracts.length > 0"
              class="bg-green-50 border-l-4 border-green-400 p-4 mb-6"
            >
              <div class="flex">
                <mat-icon class="mr-3 mt-0.5" color="primary">check_circle</mat-icon>
                <div>
                  <h4 class="text-sm font-medium text-green-800">Aucun impact négatif détecté</h4>
                  <p class="text-sm text-green-700 mt-1">
                    Tous les engagements existants sont compatibles avec le nouvel établissement principal.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Listes détaillées -->
          <div *ngIf="currentEstablishmentContracts.length > 0">
            <mat-tab-group mat-stretch-tabs="false" class="contracts-tabs">
              <mat-tab [label]="'Compatibles (' + compatibleContracts.length + ')'">
                <div class="pt-4 contracts-list-scrollable">
                  <div *ngIf="compatibleContracts.length === 0" class="text-center py-8 text-gray-500">
                    <mat-icon class="text-4xl mb-2" color="primary">info</mat-icon>
                    <p>Aucun engagement compatible trouvé</p>
                  </div>
                  <div *ngFor="let contractAnalysis of compatibleContracts" class="border-b border-gray-200 py-3">
                    <div class="flex justify-between items-start">
                      <div>
                        <h5 class="font-medium text-gray-900">{{ contractAnalysis?.contract?.object || 'N/A' }}</h5>
                        <p class="text-sm text-gray-600">
                          {{ contractAnalysis?.contract?.technicalIdentifier || 'N/A' }}
                        </p>
                      </div>
                      <mat-icon color="primary">check_circle</mat-icon>
                    </div>
                  </div>
                </div>
              </mat-tab>

              <mat-tab [label]="'Problématiques (' + problematicContracts.length + ')'">
                <div class="pt-4 contracts-list-scrollable">
                  <div *ngIf="problematicContracts.length === 0" class="text-center py-8 text-gray-500">
                    <mat-icon class="text-4xl mb-2" color="primary">check_circle</mat-icon>
                    <p>Aucun engagement problématique !</p>
                  </div>
                  <div
                    *ngFor="let contractAnalysis of problematicContracts"
                    class="border border-orange-200 rounded-lg p-4 mb-4 bg-orange-50"
                  >
                    <div class="flex justify-between items-start mb-3">
                      <div>
                        <h5 class="font-medium text-gray-900">{{ contractAnalysis?.contract?.object || 'N/A' }}</h5>
                        <p class="text-sm text-gray-600">
                          {{ contractAnalysis?.contract?.technicalIdentifier || 'N/A' }}
                        </p>
                      </div>
                      <mat-icon color="warn">error</mat-icon>
                    </div>

                    <!-- Détails d'incompatibilité -->
                    <div
                      *ngIf="contractAnalysis?.incompatibleDetails && contractAnalysis.incompatibleDetails.length > 0"
                      class="mt-3 ml-4"
                    >
                      <p class="text-sm font-medium text-orange-700 mb-2">Contrats initiaux problématiques :</p>
                      <div
                        *ngFor="let detail of contractAnalysis.incompatibleDetails"
                        class="flex items-center justify-between py-1 px-2 bg-white rounded border-l-2 border-orange-300 mb-1"
                      >
                        <div class="flex items-center flex-1">
                          <button
                            mat-icon-button
                            (click)="openContractInNewTab(detail?.initialContract?._id)"
                            matTooltip="Ouvrir le contrat dans un nouvel onglet"
                            class="mr-1"
                          >
                            <mat-icon class="text-sm text-primary">gps_fixed</mat-icon>
                          </button>
                          <div>
                            <span class="text-sm text-gray-700">
                              {{ detail?.initialContract?.technicalIdentifier || 'N/A' }}
                            </span>
                            <span class="text-xs text-gray-500 ml-2">
                              {{ detail?.initialContract?.object || 'N/A' }}
                            </span>
                          </div>
                        </div>
                        <span class="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                          {{ detail?.productCount || 0 }} produit{{ (detail?.productCount || 0) > 1 ? 's' : '' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </mat-tab>
            </mat-tab-group>
          </div>

          <!-- Message si aucun contrat -->
          <div *ngIf="currentEstablishmentContracts.length === 0" class="text-center py-8 text-gray-500">
            <mat-icon class="text-4xl mb-2" color="primary">info</mat-icon>
            <p>Aucun engagement trouvé pour cet établissement</p>
          </div>
        </div>

        <!-- Template de chargement pour l'analyse -->
        <ng-template #analysisLoadingTemplate>
          <div class="flex flex-col items-center justify-center py-12">
            <mat-spinner diameter="40"></mat-spinner>
            <p class="text-gray-600 mt-4">Analyse des engagements en cours...</p>
          </div>
        </ng-template>
      </mat-step>

      <!-- STEP 3: Confirmation -->
      <mat-step [editable]="true">
        <ng-template matStepLabel>Confirmation</ng-template>

        <!-- Récapitulatif compact -->
        <div class="bg-blue-50 border-l-4 border-blue-400 p-4">
          <h3 class="text-sm font-semibold text-blue-900 mb-3 flex items-center">
            <mat-icon class="mr-2 text-blue-700" style="font-size: 20px; height: 20px; width: 20px">info</mat-icon>
            Récapitulatif du changement
          </h3>
          <div class="space-y-2 text-sm">
            <div class="flex">
              <span class="font-medium text-gray-700 min-w-[180px]">Établissement support :</span>
              <span class="text-gray-900">
                {{ data.establishment.technicalIdentifier }} - {{ data.establishment.label }}
              </span>
            </div>
            <div class="flex" *ngIf="hasMainEstablishment">
              <span class="font-medium text-gray-700 min-w-[180px]">Ancien principal :</span>
              <span class="text-gray-600">
                {{ currentMainEstablishment?.technicalIdentifier }} - {{ currentMainEstablishment?.label }}
              </span>
            </div>
            <div class="flex">
              <span class="font-medium text-gray-700 min-w-[180px]">Nouveau principal :</span>
              <span class="text-gray-900 font-semibold" *ngIf="selectionMode === 'linked'">
                {{ newMainEstablishment?.technicalIdentifier }} - {{ newMainEstablishment?.label }}
              </span>
              <span class="text-green-700 font-semibold" *ngIf="selectionMode === 'independent'">Mode indépendant</span>
            </div>
          </div>
        </div>

        <!-- Liste des contrats problématiques -->
        <div *ngIf="problematicContracts.length > 0">
          <!-- En-tête avec total global -->
          <div class="flex items-center justify-between mb-4">
            <h4 class="text-sm font-semibold text-gray-900 flex items-center">
              <mat-icon class="mr-2 text-orange-600" style="font-size: 20px; height: 20px; width: 20px">
                warning
              </mat-icon>
              Engagements internes dont les références seront supprimées
            </h4>
            <span class="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold">
              {{ getTotalImpactedProducts() }} référence(s)
            </span>
          </div>

          <!-- Liste compacte des contrats -->
          <div class="contract-compact-list space-y-3 overflow-y-auto pr-2" style="max-height: 350px">
            <div
              *ngFor="let contract of problematicContracts; let i = index"
              class="border-l-4 border-orange-400 bg-orange-50 p-3 rounded-r"
            >
              <!-- En-tête du contrat -->
              <div class="flex items-start justify-between mb-2">
                <div class="flex-1">
                  <div class="flex items-center">
                    <span class="text-xs font-semibold text-gray-500 mr-2">{{ i + 1 }}.</span>
                    <span class="text-sm font-semibold text-gray-900">
                      [{{ contract.contract.technicalIdentifier }}]
                    </span>
                  </div>
                  <p class="text-sm text-gray-700 mt-1">{{ contract.contract.object }}</p>
                </div>
                <span class="bg-orange-200 text-orange-900 px-2 py-1 rounded text-xs font-semibold ml-2">
                  {{ getTotalProductsForContract(contract) }} réf.
                </span>
              </div>

              <!-- Détails des contrats initiaux -->
              <div *ngIf="contract.incompatibleDetails?.length" class="mt-2 ml-4 space-y-1">
                <div
                  *ngFor="let detail of contract.incompatibleDetails"
                  class="flex items-center justify-between text-xs bg-white px-2 py-1.5 rounded border border-orange-200"
                >
                  <div class="flex items-center flex-1">
                    <span class="text-gray-700 font-medium">
                      {{ detail.initialContract?.technicalIdentifier }}
                    </span>
                  </div>
                  <span class="text-gray-600">
                    {{ detail.productCount }} produit{{ detail.productCount > 1 ? 's' : '' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Avertissement -->
          <div class="mt-4 bg-orange-50 border border-orange-300 rounded p-3 text-sm text-orange-900">
            <p>
              <mat-icon class="align-middle mr-1" style="font-size: 16px; height: 16px; width: 16px">info</mat-icon>
              Ces suppressions sont
              <strong>DÉFINITIVES</strong>
              et affecteront immédiatement la disponibilité des références dans les demandes e-shop en cours.
            </p>
          </div>
        </div>

        <!-- Confirmation obligatoire si contrats problématiques -->
        <div
          *ngIf="problematicContracts.length > 0"
          class="confirmation-box bg-amber-50 border-2 border-amber-400 rounded p-3"
        >
          <mat-checkbox
            [(ngModel)]="confirmationChecked"
            [ngModelOptions]="{ standalone: true }"
            color="warn"
            class="text-sm"
          >
            <span class="text-gray-900 font-medium">
              Je comprends que cette action supprimera définitivement les références listées ci-dessus et confirme
              vouloir continuer.
            </span>
          </mat-checkbox>
        </div>

        <!-- Message si aucun problème -->
        <div *ngIf="problematicContracts.length === 0" class="bg-green-50 border-l-4 border-green-400 p-4">
          <div class="flex items-start">
            <mat-icon class="mr-2 text-green-600" style="font-size: 20px; height: 20px; width: 20px">
              check_circle
            </mat-icon>
            <div>
              <h4 class="text-sm font-semibold text-green-900">Aucun impact négatif détecté</h4>
              <p class="text-sm text-green-800 mt-1">
                Aucune référence ne sera supprimée. Vous pouvez procéder en toute sécurité.
              </p>
            </div>
          </div>
        </div>
      </mat-step>
    </mat-stepper>

    <!-- Actions dynamiques selon l'étape du stepper -->
    <mat-dialog-actions *ngIf="stepper">
      <!-- Boutons Step 1 -->
      <ng-container *ngIf="stepper.selectedIndex === 0">
        <div class="flex justify-end w-full">
          <button mat-button (click)="close()" type="button">ANNULER</button>
          <button mat-button color="primary" type="button" (click)="nextStep()" [disabled]="!canProceedToStep2()">
            SUIVANT
          </button>
        </div>
      </ng-container>

      <!-- Boutons Step 2 -->
      <ng-container *ngIf="stepper.selectedIndex === 1">
        <div class="flex justify-between w-full">
          <button mat-button (click)="previousStep()" type="button">PRÉCÉDENT</button>
          <div class="flex">
            <button mat-button (click)="close()" type="button">ANNULER</button>
            <button
              mat-button
              color="primary"
              type="button"
              (click)="nextToConfirmation()"
              [disabled]="analysisLoading"
            >
              SUIVANT
            </button>
          </div>
        </div>
      </ng-container>

      <!-- Boutons Step 3 -->
      <ng-container *ngIf="stepper.selectedIndex === 2">
        <div class="flex justify-between w-full">
          <button mat-button (click)="previousStep()" type="button">PRÉCÉDENT</button>
          <div class="flex">
            <button mat-button (click)="close()" type="button">ANNULER</button>
            <button
              mat-flat-button
              color="warn"
              type="button"
              (click)="onConfirmChanges()"
              [disabled]="loading || !canConfirm()"
            >
              <mat-icon class="mr-2">warning</mat-icon>
              {{ loading ? 'APPLICATION EN COURS...' : 'CONFIRMER ET APPLIQUER' }}
            </button>
          </div>
        </div>
      </ng-container>
    </mat-dialog-actions>
  </form>
</ng-container>

<ng-template #loadingTemplate>
  <div class="flex items-center justify-center" style="min-width: 800px; min-height: 500px">
    <mat-spinner></mat-spinner>
  </div>
</ng-template>
